import { useEffect, useId, useRef, useState } from 'react';
import { ChevronIcon } from '../icons';
import { Button } from '../Button';

/**
 * Menu déroulant accessible (bouton + role="menu") : flèches haut / bas, Début / Fin,
 * Échap (retour au bouton), Tab ferme. Les entrées « radio » indiquent l'option active.
 */
export function ActionMenu({ label, icon: Icon, items, compact }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const first = menuRef.current?.querySelector('[role^="menuitem"][aria-checked="true"]')
      ?? menuRef.current?.querySelector('[role^="menuitem"]');
    first?.focus();
    const onDown = (event) => {
      if (!menuRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const close = (focusButton = true) => {
    setOpen(false);
    if (focusButton) requestAnimationFrame(() => buttonRef.current?.focus());
  };

  const onKeyDown = (event) => {
    const entries = [...(menuRef.current?.querySelectorAll('[role^="menuitem"]') ?? [])];
    const index = entries.indexOf(document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = entries[(index + 1) % entries.length];
    else if (event.key === 'ArrowUp') next = entries[(index - 1 + entries.length) % entries.length];
    else if (event.key === 'Home') next = entries[0];
    else if (event.key === 'End') next = entries[entries.length - 1];
    else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    } else if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  return (
    <div className="viz-menu">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        icon={Icon}
        className="viz-action"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={compact ? label : undefined}
        title={label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {!compact && <span>{label}</span>}
        <ChevronIcon size={12} className="viz-action__chevron" />
      </Button>
      {open && (
        <div ref={menuRef} id={menuId} className="viz-menu__list" role="menu" aria-label={label} onKeyDown={onKeyDown}>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
              aria-checked={item.checked === undefined ? undefined : item.checked}
              className={`viz-menu__item ${item.checked ? 'is-checked' : ''}`}
              tabIndex={-1}
              onClick={() => {
                close();
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
